import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
import React, { useState, useRef } from 'react';
import _isEmpty from 'lodash/isEmpty';
import { PaginationIndicators, PaginationPlay, PaginationArrow, PaginationElements } from './components';
import PaginationInfiniteScrollTrigger from './components/PaginationInfiniteScrollTrigger';
import { PaginationContextProvider } from './contexts/pagination';
import usePagination from './hooks/use-pagination';
import { EventPropType } from '../../../analytics/types';
import { DEFAULT_ANIMATION_DELAY } from '../../../animations/animation.constants';
import AnimateHeight from '../../../animations/components/AnimatedElement/variants/AnimateHeight';
import useAnimation from '../../../animations/hooks/use-animation';
import { getAnimationWithDefaults, getTransitions } from '../../../animations/services/animation';
import { Animation } from '../../../animations/types/animation.types';
import { ChildrenPropType } from '../../types/component';
import { DEFAULT_AUTO_SCROLL_INTERVAL, DEFAULT_SLIDE_DURATION_MS } from '../Carousel/services/constants';
var defaultProps = {
  stepSize: null,
  grouping: 1,
  children: null,
  defaultActivePage: 1,
  carousel: false,
  continuous: false,
  autoScroll: false,
  autoScrollInterval: DEFAULT_AUTO_SCROLL_INTERVAL,
  animation: null,
  slide: false,
  infinite: false
};
export default function Pagination(_ref) {
  var items = _ref.items,
    pageSize = _ref.pageSize,
    stepSize = _ref.stepSize,
    grouping = _ref.grouping,
    children = _ref.children,
    defaultActivePage = _ref.defaultActivePage,
    carousel = _ref.carousel,
    continuous = _ref.continuous,
    autoScroll = _ref.autoScroll,
    autoScrollInterval = _ref.autoScrollInterval,
    animationProp = _ref.animation,
    slide = _ref.slide,
    event = _ref.event,
    infinite = _ref.infinite;
  var paginationOptions = {
    defaultActivePage: defaultActivePage,
    carousel: carousel,
    continuous: continuous,
    autoScroll: autoScroll,
    autoScrollInterval: autoScrollInterval,
    stepSize: stepSize,
    grouping: grouping,
    slide: slide,
    infinite: infinite
  };
  var ref = useRef(null);
  var _useState = useState('auto'),
    _useState2 = _slicedToArray(_useState, 2),
    height = _useState2[0],
    setHeight = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    sliding = _useState4[0],
    setSliding = _useState4[1];
  var pagination = usePagination(items, pageSize, paginationOptions);
  var _onPreviousPage = pagination.onPreviousPage,
    _onNextPage = pagination.onNextPage,
    _onPageChange = pagination.onPageChange;
  var _getAnimationWithDefa = getAnimationWithDefaults(animationProp),
    animationName = _getAnimationWithDefa.name,
    duration = _getAnimationWithDefa.duration,
    transitionProps = _getAnimationWithDefa.transitionProps,
    targetSelector = _getAnimationWithDefa.targetSelector;
  var animated = !_isEmpty(animationName);
  var _useAnimation = useAnimation(getTransitions(animationName), {
      duration: duration
    }),
    startAnimation = _useAnimation.startAnimation,
    _useAnimation$state = _useAnimation.state,
    transition = _useAnimation$state.transition,
    status = _useAnimation$state.status;
  function onSlidePageChange(pageChangeFunction) {
    if (sliding) {
      return;
    }
    setSliding(true);
    pageChangeFunction();
    setTimeout(function () {
      setSliding(false);
    }, DEFAULT_SLIDE_DURATION_MS);
  }
  function onAnimatedPageChange(pageChangeFunction) {
    startAnimation();
    setTimeout(function () {
      setHeight(ref.current.clientHeight);
      setTimeout(function () {
        // ensure no race condition between the height animation and the change element animation
        pageChangeFunction();
        setHeight('auto');
      }, DEFAULT_ANIMATION_DELAY);
    }, duration);
  }
  function onPageChangeAnimatedWrapper(pageChangeFunction) {
    if (slide) {
      onSlidePageChange(pageChangeFunction);
    } else if (animated) {
      onAnimatedPageChange(pageChangeFunction);
    } else {
      pageChangeFunction();
    }
  }
  var animation = {
    animated: animated,
    name: animationName,
    status: status,
    transition: transition,
    duration: duration,
    transitionProps: transitionProps,
    targetSelector: targetSelector
  };
  var finalPagination = _objectSpread(_objectSpread({}, pagination), {}, {
    onPreviousPage: function onPreviousPage() {
      onPageChangeAnimatedWrapper(_onPreviousPage);
    },
    onNextPage: function onNextPage() {
      onPageChangeAnimatedWrapper(_onNextPage);
    },
    onPageChange: function onPageChange(e, params) {
      onPageChangeAnimatedWrapper(function () {
        _onPageChange(e, params);
      });
    }
  });
  var contextValue = {
    pagination: finalPagination,
    animation: animation,
    event: event
  };
  if (animated) {
    return /*#__PURE__*/React.createElement("div", {
      ref: ref
    }, /*#__PURE__*/React.createElement(AnimateHeight, {
      className: "animated-pagination",
      duration: duration,
      height: height,
      style: {
        overflow: 'hidden'
      }
    }, /*#__PURE__*/React.createElement(PaginationContextProvider, {
      value: contextValue
    }, children)));
  }
  return /*#__PURE__*/React.createElement(PaginationContextProvider, {
    value: contextValue
  }, children);
}
Pagination.defaultProps = defaultProps;
Pagination.Play = PaginationPlay;
Pagination.Arrow = PaginationArrow;
Pagination.Indicators = PaginationIndicators;
Pagination.Elements = PaginationElements;
Pagination.InfiniteScrollTrigger = PaginationInfiniteScrollTrigger;